import {IIntegerRange} from '@colibrio/colibrio-reader-framework/colibrio-core-base';
import {ILocator, Locator} from '@colibrio/colibrio-reader-framework/colibrio-core-locator';
import {MediaFragmentSelector} from '@colibrio/colibrio-reader-framework/colibrio-core-selector-media';

import {
    IContentLocation,
    ISyncMediaPlayer,
    ISyncMediaTimeline,
} from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-base';
import { VanillaReaderEventBus,} from '../VanillaReaderEventBus/VanillaReaderEventBus';
import {
    IVanillaReaderReadingProgressionTimeline, IVanillaReaderReadingProgressionTimelineData,
    IVanillaReaderReadingProgressionTimelinePositionData,
    VanillaProgressEventCallback,
    VanillaTimelineReadyEventCallback
} from './VanillaReaderModel';

export class VanillaReaderAudiobookProgressionTimeline implements IVanillaReaderReadingProgressionTimeline {
    private readonly _colibrioTimelineInstance: ISyncMediaTimeline;
    private _timelineUpdateIntentCallback: VanillaProgressEventCallback | undefined = undefined;

    constructor(private _colibrioSyncMediaPlayer: ISyncMediaPlayer) {
        this._colibrioTimelineInstance = this._colibrioSyncMediaPlayer.getTimeline();
    }

    fetchTimelinePosition(locator: ILocator): Promise<number> {
        let startPosition = locator.getSelectors()[0];

        if (!(startPosition instanceof MediaFragmentSelector)) {
            return Promise.reject();
        }

        let startMs = startPosition.getTimeInterval()?.startMs;

        if (typeof startMs !== 'number') {
            return Promise.reject('VanillaReaderAudiobookProgressionTimeline.updateTimelinePosition(): unable to resolve start position for locator ' + locator.toString());
        }

        return Promise.resolve(startMs);
    }

    async fetchContentLocation(timelinePosition: number): Promise<IContentLocation> {
        let mediaFragmentSelector = new MediaFragmentSelector({
            timeInterval: {
                startMs: timelinePosition,
            },
        }, []);
        let locator = new Locator(this._colibrioTimelineInstance.getReaderPublication().getDefaultLocatorUrl(), [mediaFragmentSelector]);
        let position = await this._colibrioTimelineInstance.fetchTimelinePosition(locator);
        return this._colibrioTimelineInstance.fetchContentLocation(position);
    }

    async fetchContentLocationAsRange(_start: number, _end: number): Promise<IContentLocation | undefined> {
        return Promise.resolve(undefined);
    }

    async fetchTimelineRange(_location: ILocator): Promise<IIntegerRange> {
        return Promise.reject('Not implemented for VanillaReaderAudiobookProgressionTimeline');
    }

    updateTimelinePosition(locator: ILocator): void {
        const updateFn = () => {
            let startPosition = locator.getSelectors()[0];
            if (startPosition instanceof MediaFragmentSelector) {
                let startMs = startPosition.getTimeInterval()?.startMs;

                if (typeof startMs !== 'number') {
                    console.warn('VanillaReaderAudiobookProgressionTimeline.updateTimelinePosition(): unable to resolve start position for locator ' + locator.toString());
                    return;
                }

                if (this._timelineUpdateIntentCallback) {
                    this._timelineUpdateIntentCallback(startMs);
                }
            }
        };

        if (!this._colibrioTimelineInstance) {
            VanillaReaderEventBus.addEventListener("TIMELINE_READY", updateFn, true);
        } else {
            updateFn();
        }

    }

    getLength(): number {
        return this._colibrioTimelineInstance.getApproximateDurationMs();
    }

    toSerializedData(): undefined {
        return undefined;
    }

    onTimelineReady(callback: VanillaTimelineReadyEventCallback): void {
        Promise.resolve().then(() => {
            return this._fetchDocumentPositionData(this._colibrioTimelineInstance).then( (sections) => {
                callback({
                    length: this._colibrioTimelineInstance.getApproximateDurationMs(),
                    unit: undefined,
                    sections: sections
                });
            })
        }).catch();
    }

    onTimelinePositionUpdated(callback: VanillaProgressEventCallback): void {
        this._timelineUpdateIntentCallback = callback;
    }

    isReady(): boolean {
        return this._colibrioSyncMediaPlayer.isReady();
    }

    getBackingTimelineImplementation(): ISyncMediaTimeline {
        return this._colibrioTimelineInstance;
    }

    serialize(): Promise<IVanillaReaderReadingProgressionTimelineData | undefined> {
        return Promise.resolve(undefined);
    }

    /**
     *
     * PRIVATE METHODS
     *
     * */
    private async _fetchDocumentPositionData(timeline: ISyncMediaTimeline): Promise<IVanillaReaderReadingProgressionTimelinePositionData[]> {
        let documentContentLocations: IVanillaReaderReadingProgressionTimelinePositionData[] = [];

        let approxStartTimeMs = 0;
        let approxEndTimeMs = 0;

        for(let segment of timeline.getSegments()) {
            approxStartTimeMs = approxEndTimeMs;
            approxEndTimeMs += segment.getApproximateDurationMs();
            let timelinePosition = timeline.getTimelinePositionFromApproximateOffsetMs(approxEndTimeMs);
            let contentLocation = await timeline.fetchContentLocation(timelinePosition);

            documentContentLocations.push({
                contentPositionData: {
                    locator: contentLocation.getLocator().toString(this._colibrioTimelineInstance.getReaderPublication().getDefaultLocatorUrl()),
                    timestamp: Date.now()
                },
                range: {start: approxStartTimeMs, end: approxEndTimeMs}
            });

        }

        return documentContentLocations;
    }
}
